import {useQuery} from "@apollo/client";
import classNames from "classnames";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {FormModal} from "@/components/form/overlay/FormModal.tsx";
import {LinkedSchedulesActions} from "@/components/multiServiceSelect/LinkedSchedulesActions.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {EmptyTable} from "@/components/table/subComponents/EmptyTable/EmptyTable.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec} from "@/components/table/Table.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {useSorting} from "@/lib/hooks/useSorting.ts";
import {GQL_MAINTENANCE_SCHEDULES_LIST, MaintenanceListResponse} from "@/lib/queries/maintenance.ts";
import {MaintenanceSchedule} from "@/lib/types/maintenance.ts";
import {MaintenanceSchedulesStatePill} from "@/pages/maintenance/maintenance-dashboard/tabs/schedules/MaintenanceSchedulesTable/components/MaintenanceSchedulesStatePill.tsx";
import {TimeUntil} from "@/pages/maintenance/maintenance-dashboard/tabs/schedules/MaintenanceSchedulesTable/components/TimeUntil.tsx";
import {getRecurrenceDisplay} from "@/pages/maintenance/maintenance.utils.ts";

import styles from "./LinkedSchedulesModal.module.scss";

interface LinkedSchedulesModalProps {
  show: boolean;
  onHide: () => void;
  checkId: number;
  checkName: string;
  excludeScheduleId?: number;
}

export const LinkedSchedulesModal = ({
  show,
  onHide,
  checkId,
  checkName,
  excludeScheduleId,
}: LinkedSchedulesModalProps) => {
  const {user} = useCurrentUserContext();

  const {sorting, setSorting, sortingString} = useSorting({defaultSort: "name", useUrlParams: false});

  const {data, loading} = useQuery<MaintenanceListResponse>(GQL_MAINTENANCE_SCHEDULES_LIST, {
    variables: {
      filters: {services: [checkId.toString()]},
      sort: sortingString,
    },
    skip: !show,
  });

  const items = data?.response?.items || [];
  const schedules = excludeScheduleId ? items.filter((s) => s.id !== excludeScheduleId) : items;

  const specs: ColumnSpec<MaintenanceSchedule>[] = [
    {
      fieldName: "name",
      fieldTitle: "Name",
      enableSorting: true,
      meta: {
        headerClassName: styles.nameColumn,
      },
      accessor: (schedule: MaintenanceSchedule) => (
        <Tooltip tooltipContent={schedule.name} delayMs={500} onModal>
          <div className="ellipsis-overflow pulseTextBodySmall pulse-color-gray-900">{schedule.name}</div>
        </Tooltip>
      ),
    },
    {
      fieldName: "status",
      fieldTitle: "Status",
      enableSorting: true,
      meta: {
        headerClassName: classNames("text-nowrap", styles.statusColumn),
      },
      accessor: (schedule: MaintenanceSchedule) => <MaintenanceSchedulesStatePill schedule={schedule} />,
    },
    {
      fieldName: "next_occurrence",
      fieldTitle: "Next Scheduled",
      meta: {
        headerClassName: classNames("text-nowrap", styles.nextScheduledColumn),
      },
      accessor: (schedule: MaintenanceSchedule) => <TimeUntil schedule={schedule} user={user!} />,
    },
    {
      fieldName: "type",
      fieldTitle: "Repeat",
      meta: {
        headerClassName: styles.repeatColumn,
      },
      accessor: (schedule: MaintenanceSchedule) => {
        const recurrenceDisplay = getRecurrenceDisplay(schedule);
        return (
          <Tooltip tooltipContent={recurrenceDisplay} delayMs={500}>
            <div className="ellipsis-overflow pulseTextBodySmall pulse-color-gray-900">{recurrenceDisplay}</div>
          </Tooltip>
        );
      },
    },
    {
      fieldName: "actions",
      meta: {
        headerClassName: styles.actionColumn,
      },
      accessor: (schedule: MaintenanceSchedule) => (
        <LinkedSchedulesActions scheduleId={schedule.id} scheduleName={schedule.name} checkId={checkId} />
      ),
    },
  ];

  return (
    <FormModal
      title={`Check: ${checkName}`}
      cancelBtnText="Close"
      hasSubmitButton={false}
      onCancel={onHide}
      show={show}
      size="lg"
      formId="linked-schedules-modal"
    >
      {loading ? (
        <LoadingContainer />
      ) : (
        <Table
          rows={schedules}
          specs={specs}
          initialState={{columnPinning: {right: ["actions"]}}}
          isLoading={loading}
          fixedWidth
          minWidth={"742px"}
          sorting={sorting}
          onSortingChange={setSorting}
          noDataComponent={
            <EmptyTable colSpan={specs.length}>This check is not linked to any maintenance schedules.</EmptyTable>
          }
        />
      )}
    </FormModal>
  );
};
